import React, { forwardRef, useCallback } from 'react';
import { clsx } from 'clsx';
import { designTokens } from '../../styles/design-tokens';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  haptic?: boolean;
  hapticPattern?: number[]; // [50, 50, 100] = short-short-long
  celebrateOnClick?: boolean;
  fullWidth?: boolean;
}

// Size specifications ensuring touch-friendly targets
const sizeClasses = {
  sm: {
    height: 32,
    minWidth: 64,
    paddingX: 12,
    fontSize: 14,
    iconSize: 16,
    className: 'h-8 min-w-[64px] px-3 text-sm gap-1.5'
  },
  md: {
    height: 40,
    minWidth: 80,
    paddingX: 16,
    fontSize: 16,
    iconSize: 20,
    className: 'h-10 min-w-[80px] px-4 text-base gap-2'
  },
  lg: {
    height: 48, // iOS HIG minimum
    minWidth: 120,
    paddingX: 24,
    fontSize: 18,
    iconSize: 24,
    className: 'h-12 min-w-[120px] px-6 text-lg gap-2.5'
  }
};

const variantClasses = {
  primary: 'bg-[#4B5FDC] text-white hover:bg-[#3A4BC9] active:bg-[#2938B6] focus:ring-[#4B5FDC]',
  secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 active:bg-gray-400 focus:ring-gray-500',
  ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-500',
  danger: 'bg-[#DC2626] text-white hover:bg-[#B91C1C] active:bg-[#991B1B] focus:ring-[#DC2626]',
};

// Haptic feedback patterns
const hapticPatterns = {
  success: [50, 30, 100],    // short-short-long
  error: [100, 100, 100],    // three equal pulses
  tap: [30],                 // single short tap
  double: [30, 30],          // double tap
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  className = '',
  type = 'button',
  leftIcon,
  rightIcon,
  haptic = false,
  hapticPattern,
  celebrateOnClick = false,
  fullWidth = false,
  onClick,
  ...props
}, ref) => {
  const baseStyles = `
    inline-flex items-center justify-center font-medium rounded-lg
    transition-all duration-200 transform
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    active:scale-[0.98]
  `;

  // Handle haptic feedback
  const triggerHaptic = useCallback((pattern: number[] = hapticPatterns.tap) => {
    if ('vibrate' in navigator && haptic) {
      navigator.vibrate(pattern);
    }
  }, [haptic]);

  // Handle click with haptic feedback and celebration
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (haptic) {
      triggerHaptic(hapticPattern || hapticPatterns.tap);
    }

    if (celebrateOnClick && !disabled && !isLoading) {
      // Add celebration class temporarily
      const button = e.currentTarget;
      button.classList.add('animate-celebration');
      setTimeout(() => {
        button.classList.remove('animate-celebration');
      }, 600);
    }

    onClick?.(e);
  }, [onClick, haptic, hapticPattern, celebrateOnClick, disabled, isLoading, triggerHaptic]);

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      className={clsx(
        baseStyles,
        variantClasses[variant],
        sizeClasses[size].className,
        fullWidth && 'w-full',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {/* Loading spinner */}
      {isLoading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      
      {/* Left icon */}
      {leftIcon && !isLoading && (
        <span className="inline-flex">{leftIcon}</span>
      )}
      
      {/* Button text */}
      {children}
      
      {/* Right icon */}
      {rightIcon && (
        <span className="inline-flex">{rightIcon}</span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

// Export haptic patterns for use elsewhere
export { hapticPatterns };